import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { CrmGraphql } from '../../shared/constants/graphql-constants';
import { DropdownField } from '../../shared/form-field/dropdown-field';
import { FormField } from '../../shared/form-field/form-field';
import { TextboxField } from '../../shared/form-field/textbox-field';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DropdownDataService } from '../../services/dropdown.data.service';
import { SecurityTypeService } from '../../administration/security/security.service';

const inquiryQuery = gql`
  query ($id: Int!) {
    getContractProfileById(id: $id) {
      contractsId
      contractInquiry {
        contractInquiryEventsId
        contractsId
        status
        assignedTo
        userName
        content
        enteredBy
        enteredTime
        contractInquiryEventsIdOriginal
      }
    }
  }
`;

const createUpdateContractInquiry = gql`
  mutation (
    $contractsId: Int!
    $contractNumber: String
    $status: String
    $assignedTo: String
    $content: String
    $enteredBy: String
    $contractInquiryEventsIdOriginal: Int
    $isDeleted: Boolean
    $insertUserName: String
    $addedTimeStamp: Date
  ) {
    createUpdateContractInquiry(
      contractInquiry: {
        contractsId: $contractsId
        contractNumber: $contractNumber
        status: $status
        assignedTo: $assignedTo
        content: $content
        enteredBy: $enteredBy
        contractInquiryEventsIdOriginal: $contractInquiryEventsIdOriginal
        isDeleted: $isDeleted
        insertUserName: $insertUserName
        addedTimeStamp: $addedTimeStamp
      }
    ) {
      contractInquiryEventsId
      contractsId
      contractNumber
      status
      assignedTo
      content
      enteredBy
      enteredTime
      contractInquiryEventsIdOriginal
      insertUserName
      addedTimeStamp
    }
  }
`;

const getUserDetails = gql`
  query ($azureUserId: String) {
    getUserDetails(azureUserId: $azureUserId) {
      key
      value
    }
  }
`;

@Injectable()
export class InquiryTabService {
  templateDetails = {};
  typeMapping = {};
  isLoading: boolean;
  constructor(
    private apollo: Apollo,
    private dropdownDataService: DropdownDataService,
    private securityService: SecurityTypeService,
  ) {}

  getInquiryFormModalFields() {
    return this.securityService
      .getRolePermissionSearch({
        permissionCategory: 'Contract',
        permissionDescription: 'Inquiry Queue Roles',
        limit: 1000,
        offset: 0,
      })
      .pipe(
        map(({ data }) => {
          const allowedRoles = new Set<string>(
            (data?.getRolePermissionSearch || [])
              .map((r: any) => String(r.roleName || '').trim())
              .filter(Boolean),
          );

          let optionsUserList: Array<{ key: string; value: string }> = [];

          if (this.dropdownDataService.userListData && Array.isArray(this.dropdownDataService.userListData)) {
            optionsUserList = this.dropdownDataService.userListData
              .filter((x) => {
                if (x.status) {
                  const status = String(x.status).toUpperCase();
                  if (status !== 'ACTIVE') return false;
                }
                if (allowedRoles.size > 0) {
                  if (!x.attachedRole) return false;
                  const userRole = String(x.attachedRole).trim();
                  const hasAllowedRole = allowedRoles.has(userRole);
                  if (!hasAllowedRole) return false;
                } else {
                  return false;
                }
                return true;
              })
              .map((x) => ({
                key: x.key,
                value: x.value,
              }))
              .sort((a, b) => a.key.localeCompare(b.key));
          }

          optionsUserList.unshift({ key: '', value: '' });

          const formFields: FormField<string>[] = [
            new DropdownField({
              required: true,
              key: 'assignedTo',
              label: 'Assigned To',
              filter: true,
              isSingleselect: true,
              options: optionsUserList,
              order: 0,
            }),
            new DropdownField({
              required: true,
              key: 'status',
              label: 'Status',
              options: [
                { key: 'Open', value: 'Open' },
                { key: 'Closed', value: 'Closed' },
                { key: 'InProgress', value: 'InProgress' },
                { key: 'New', value: 'New' },
                { key: 'OnHold', value: 'OnHold' },
              ],
              isSingleselect: true,
              order: 1,
            }),
            new TextboxField({
              required: true,
              key: 'content',
              label: 'Content',
              order: 2,
            }),
          ];

          return formFields.sort((a, b) => a.order - b.order);
        }),
      );
  }

  getInquiry = (searchParams) =>
    this.apollo.use(CrmGraphql.QueryNameSpace).watchQuery<any>({
      query: inquiryQuery,
      variables: { id: searchParams },
      fetchPolicy: 'network-only',
      // tslint:disable-next-line: semicolon
    }).valueChanges;

  createUpdateContractInquiry = (contractInquiry) =>
    this.apollo.use(CrmGraphql.NameSpace).mutate({
      mutation: createUpdateContractInquiry,
      variables: {
        ...contractInquiry,
      },
      // tslint:disable-next-line: semicolon
    });

  getInquiryQueueEmail = (searchParams) =>
    this.apollo.use(CrmGraphql.QueryNameSpace).watchQuery<any>({
      query: getUserDetails,
      variables: { ...searchParams },
      fetchPolicy: 'network-only',
      // tslint:disable-next-line: semicolon
    }).valueChanges;

  getEmailRequest(emailAddress, contractNumber, dealerName) {
    const templateName = 'TEMPLATE_ShortCode_template1';

    if (emailAddress === '') {
      // If there is no email address, skip.
      return null;
    }
    const inquiryQueueDetails = this.addInquiryQueueDetails(templateName, emailAddress);
    const request = {
      operation: 'send_template',
      data: {
        from: 'donotreply@newleafsc.net',
        receivers: [
          {
            Email: emailAddress,
            FirstName: 'FirstName',
            LastName: 'LastName',
          },
        ],
        variables: {
          custom_subject: `New Inquiry Assignment - Contract # ${contractNumber} - ${dealerName}`,
          custom_text: ` `,
        },
        template: templateName,
      },
      transactionDetails: inquiryQueueDetails,
    };
    return request;
  }

  addInquiryQueueDetails(templateName, emailAddress) {
    return {
      EmailAddress: emailAddress,
      EmailTemplate: templateName,
      InsertNote: false,
    };
  }
}
